/**
 * ForeignKeyPopoverContent
 *
 * Renders the row-data block inside ForeignKeyPopover (loading/error/empty/row
 * states for a single referenced row). Kept as a separate file to keep
 * ForeignKeyPopover under the project's per-file line guideline.
 */

import type { ForeignKeyRowData } from '../hooks/useForeignKeyData';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';

type ForeignKeyPopoverContentProps = {
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly data: ForeignKeyRowData | undefined;
};

export function ForeignKeyPopoverContent({ isLoading, error, data }: ForeignKeyPopoverContentProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <RandomDotmSpinner size={16} className="text-muted-foreground" />
        <span className="ml-2 text-xs text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-xs text-destructive py-2">
        Failed to load: {error.message}
      </div>
    );
  }

  if (!data?.row) {
    return (
      <div className="text-xs text-muted-foreground py-2 text-center">
        No matching row found
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {data.columns.map((col) => {
        const cellValue = data.row![col.name];
        const isNullish = cellValue === null || cellValue === undefined;
        const displayValue = isNullish
          ? 'NULL'
          : typeof cellValue === 'object'
          ? JSON.stringify(cellValue)
          : String(cellValue);
        // F14: a host's display seam (storage date-times) shapes what is shown; the title keeps the exact value.
        const shownValue = isNullish ? displayValue : data.displayText?.(cellValue, col.name) ?? displayValue;

        return (
          <div key={col.name} className="grid grid-cols-[100px_1fr] gap-2 text-xs">
            <div className="font-medium text-muted-foreground truncate" title={col.name}>
              {col.name}
            </div>
            <div
              className={`truncate ${isNullish ? 'text-muted-foreground/60 italic' : ''}`}
              title={displayValue}
            >
              {shownValue}
            </div>
          </div>
        );
      })}
    </div>
  );
}
